import { Component } from '@theme/component';
import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A floating button that scrolls the page back to the top.
 *
 * Appears once the shopper has scrolled roughly one viewport height down the
 * page scroll container and hides again near the top. The scroll container is
 * `.page-wrapper` in the squeeze layout (desktop ≥990px) and the document root
 * on mobile, so the scroll listener is rebound whenever that boundary flips.
 *
 * @example
 * <scroll-to-top-component class="scroll-to-top" data-visible="false">
 *   <button type="button" on:click="/scrollToTop" aria-label="...">...</button>
 * </scroll-to-top-component>
 *
 * @extends {Component}
 */
class ScrollToTopComponent extends Component {
  /** @type {AbortController} Aborts listeners tied to the component lifetime. */
  #abortController = new AbortController();

  /** @type {AbortController | null} Aborts the scroll listener, recreated when the scroll container changes. */
  #scrollAbortController = null;

  /** @type {boolean} Whether a visibility update is already scheduled for the next frame. */
  #frameScheduled = false;

  /** @type {boolean} */
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    // Recreate the controller each connect: disconnectedCallback aborts it, and an
    // aborted signal makes addEventListener a no-op, so a reconnect would otherwise
    // silently drop the media-query listener.
    this.#abortController = new AbortController();

    scrollContainerMediaQuery.addEventListener('change', this.#bindScrollListener, {
      signal: this.#abortController.signal,
    });

    this.#bindScrollListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollAbortController?.abort();
    this.#abortController.abort();
  }

  /**
   * Re-applies the visibility state after a Section Rendering API re-render,
   * since morph may reset the host's `data-visible` attribute to the
   * server-rendered default.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyVisibility();
  }

  /**
   * Scrolls the page scroll container back to the top.
   * Bound declaratively via `on:click="/scrollToTop"`.
   */
  scrollToTop() {
    getScrollContainer().scrollTo({
      top: 0,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * (Re)binds the scroll listener to the current scroll event target.
   * Runs on connect and whenever the 990px squeeze-layout media query flips,
   * because the scroll container switches between `.page-wrapper` and the
   * document root.
   */
  #bindScrollListener = () => {
    this.#scrollAbortController?.abort();
    this.#scrollAbortController = new AbortController();

    getScrollEventTarget().addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#scrollAbortController.signal,
    });

    this.#updateVisibility();
  };

  /**
   * Throttles visibility updates to one per animation frame.
   */
  #handleScroll = () => {
    if (this.#frameScheduled) return;
    this.#frameScheduled = true;

    requestAnimationFrame(() => {
      this.#frameScheduled = false;
      this.#updateVisibility();
    });
  };

  /**
   * Shows the button after ~1 viewport height of scrolling, hides it near the top.
   */
  #updateVisibility() {
    const container = getScrollContainer();
    this.#visible = container.scrollTop > container.clientHeight;
    this.#applyVisibility();
  }

  /**
   * Syncs the host's `data-visible` attribute with the tracked state.
   */
  #applyVisibility() {
    const value = this.#visible ? 'true' : 'false';
    if (this.dataset.visible !== value) this.dataset.visible = value;
  }
}

if (!customElements.get('scroll-to-top-component')) {
  customElements.define('scroll-to-top-component', ScrollToTopComponent);
}
